<template>
  <div class="w-screen h-screen flex items-center justify-center">
    <Spinner :size="28" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getTokens, isSignedIn, checkAccessToken } from '@dacr/auth'
import Spinner from '@/shared/components/Spinner.vue'

const route = useRoute()
const router = useRouter()
const error = ref<string | null>(null)

onMounted(async () => {
  // If not signed in, redirect to login with return URL
  if (!isSignedIn()) {
    const currentUrl = window.location.href
    router.push(`/login?redirect=${encodeURIComponent(currentUrl)}`)
    return
  }

  // Ensure access token is valid
  await checkAccessToken()
  const { accessToken } = getTokens()

  if (!accessToken) {
    error.value = 'No access token available'
    return
  }

  // Forward all query params to backend
  const params = new URLSearchParams(route.query as Record<string, string>)

  try {
    const response = await fetch(
      `${import.meta.env.VITE_AUTH_API}/oauth/authorize?${params}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )

    const data = await response.json()

    if (!response.ok || data.error) {
      return
    }

    // Redirect to the callback with the code
    const callbackUrl = new URL(data.redirect_uri)
    callbackUrl.searchParams.set('code', data.code)
    if (data.state) {
      callbackUrl.searchParams.set('state', data.state)
    }

    router.push('/auth/success')
    window.location.href = callbackUrl.toString()
  } catch (err) {
    console.error('OAuth authorize error:', err)
    error.value = 'Failed to authorize'
  }
})
</script>
